import React, { Suspense, useState } from "react";

import LockedBadge from "@impero-com/common/img/badge-locked.svg";
import Globe from "@impero-com/common/img/globe.svg";
import { Meta } from "@impero-com/common/utils/header";
import { t } from "@impero-com/common/utils/i18n";
import {
    PageSpinner,
    SidebarTopBar,
    Svg,
    useBelowBreakpoint,
    useLocalStorage,
} from "@impero-com/ui";
import type { NavRowId } from "@impero-com/ui/domain";
import MenuIcon from "@impero-com/ui/img/menu-icon.svg";
import { Layout, Menu } from "antd";
import * as Cookies from "es-cookie";
import { Link, useLocation } from "react-router-dom";

import { usePostSetLocizeCookie } from "api/generated/utils/utils";
import { useOrgUserProfileCookie } from "utils/profileCookie";

import { OrgApplicationBar } from "../components/applicationBar/orgApplicationBar";
import { isLegacyPath } from "../legacyRoutes";

import { NavIcon } from "./navigation/navIcons";
import { NavSidebar } from "./navigation/NavSidebar";
import type { ResolvedNavItem } from "./navigation/resolveNavItems";
import { useNavMenu } from "./navigation/useNavMenu";
import { useNewMenu } from "./navigation/useNewMenu";
import * as theme from "./theme.css";

const { Content, Sider } = Layout;

interface BodyProps {
    title: string;
    /**
     * The menu row this page belongs to. A page without a row of its own names
     * the row it sits under; `null` says it has none.
     */
    navId: NavRowId | null;
    defaultAppBar?: boolean;
    routerLinks: boolean;
    children?: React.ReactNode;
}

interface SmartLinkProps {
    routerLinks: boolean;
    href: string;
    children: React.ReactNode;
}

function SmartLink({ routerLinks, href, children }: SmartLinkProps) {
    return routerLinks ? (
        <Link to={href}>{children}</Link>
    ) : (
        <a href={href}>{children}</a>
    );
}

interface FlatMenuLabelProps {
    item: ResolvedNavItem;
    collapsed: boolean;
    routerLinks: boolean;
}

function FlatMenuLabel({ item, collapsed, routerLinks }: FlatMenuLabelProps) {
    // A locked row stays visible so users can ask for the feature; Pendo owns the modal.
    if (item.locked) {
        if (collapsed) {
            return (
                <NavIcon
                    src={item.lockedIcon ?? item.icon}
                    className={theme.collapsedLockedIcon}
                />
            );
        }

        return (
            <div className={theme.lockedContainer}>
                <div className={theme.lockedInnerContainer}>
                    <NavIcon src={item.icon} />
                    <span className={theme.leftMenuItemText}>{item.label}</span>
                </div>
                <Svg className={theme.lockedBadge} src={LockedBadge} />
            </div>
        );
    }

    const content = (
        <>
            <NavIcon src={item.icon} className={theme.menuIcon} />
            <span className={theme.leftMenuItemText}>{item.label}</span>
        </>
    );

    return item.href === undefined ? (
        content
    ) : (
        <SmartLink
            routerLinks={routerLinks && !isLegacyPath(item.href)}
            href={item.href}
        >
            {content}
        </SmartLink>
    );
}

export function Body({
    title,
    navId,
    defaultAppBar,
    routerLinks,
    children,
}: BodyProps) {
    const orgProfileCookie = useOrgUserProfileCookie();
    const newMenu = useNewMenu();
    // The rail opens expanded like the legacy one; the old menu opens collapsed.
    const [collapsed, setCollapsed] = useLocalStorage(
        "sidebar:collapsed",
        !newMenu
    );
    const [narrowScreen, setNarrowScreen] = useState(false);
    const phone = useBelowBreakpoint("md");
    // The phone menu covers the page, so it starts closed and is never stored.
    const [phoneMenuOpen, setPhoneMenuOpen] = useState(false);
    const { key: pageKey } = useLocation();
    const [phoneMenuPageKey, setPhoneMenuPageKey] = useState(pageKey);

    if (phoneMenuPageKey !== pageKey) {
        setPhoneMenuPageKey(pageKey);
        setPhoneMenuOpen(false);
    }

    const { mutateAsync: setLocizeCookie } = usePostSetLocizeCookie();

    const toggle = () => {
        setCollapsed((current) => !current);
    };

    const togglePhoneMenu = () => {
        setPhoneMenuOpen((open) => !open);
    };

    const canLocize = orgProfileCookie ? orgProfileCookie.canLocalize : false;

    const toggleLocize = () => {
        const done = () => {
            window.location.reload();
        };
        if (Cookies.get("locize_api_key")) {
            const domain = window.location.hostname;
            const parentDomain = domain.replace(/^[^\.]+\./, "");
            Cookies.remove("locize_api_key");
            Cookies.remove("locize_api_key", {
                domain: "." + parentDomain,
                path: "/",
            });
            Cookies.remove("locize_api_key", {
                domain: parentDomain,
                path: "/",
            });
            done();
        } else {
            setLocizeCookie().then(done);
        }
    };

    const { flat, grouped } = useNavMenu();
    const userLoggedIn = !!orgProfileCookie;

    const menuItems = [
        ...flat.map((item) => ({
            key: item.id,
            className: item.locked
                ? theme.leftMenuItemDisabled
                : theme.leftMenuItem,
            label: (
                <FlatMenuLabel
                    item={item}
                    collapsed={collapsed}
                    routerLinks={routerLinks}
                />
            ),
        })),
        ...(canLocize
            ? [
                  {
                      key: "locize",
                      className: theme.leftMenuItem,
                      onClick: toggleLocize,
                      label: (
                          <>
                              <Svg
                                  className={theme.menuIcon}
                                  src={Globe}
                                  style={{
                                      fill: Cookies.get("locize_api_key")
                                          ? "#2196F3"
                                          : "white",
                                  }}
                              />
                              <span className={theme.leftMenuItemText}>
                                  {"I18n"}
                              </span>
                          </>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <>
            <Meta title={title} />
            <Layout className={theme.leftMenuContainer}>
                <Layout hasSider={userLoggedIn}>
                    {userLoggedIn ? (
                        <>
                            <SidebarTopBar
                                title={title}
                                menuLabel={t("Navigation_ToggleMenu")}
                                onToggleMenu={
                                    newMenu ? togglePhoneMenu : toggle
                                }
                            />
                            {newMenu ? (
                                <NavSidebar
                                    items={grouped}
                                    activeId={navId}
                                    routerLinks={routerLinks}
                                    collapsed={
                                        phone ? !phoneMenuOpen : collapsed
                                    }
                                    onToggleCollapsed={toggle}
                                />
                            ) : (
                                <Sider
                                    className={theme.leftMenuSider}
                                    collapsible
                                    trigger={null}
                                    collapsed={collapsed}
                                    width="270px"
                                    collapsedWidth={narrowScreen ? 0 : 70}
                                    breakpoint="md"
                                    onBreakpoint={setNarrowScreen}
                                >
                                    <div className={theme.leftMenuSiderContent}>
                                        <div>
                                            <div
                                                onClick={toggle}
                                                className={
                                                    theme.leftMenuTopIcon
                                                }
                                            >
                                                <Svg
                                                    className={
                                                        theme.siderMenuIcon
                                                    }
                                                    src={MenuIcon}
                                                />
                                                <span
                                                    className={
                                                        theme.menuButtonText
                                                    }
                                                >
                                                    MENU
                                                </span>
                                            </div>
                                            <Menu
                                                theme="dark"
                                                mode="inline"
                                                className={theme.leftMenu}
                                                items={menuItems}
                                            />
                                        </div>
                                    </div>
                                </Sider>
                            )}
                        </>
                    ) : (
                        ""
                    )}
                    <Layout>
                        <Content
                            id="body-content"
                            className={theme.contentContainer}
                        >
                            {defaultAppBar ? (
                                <OrgApplicationBar
                                    title={title}
                                    routerLinks={routerLinks}
                                >
                                    <Suspense fallback={<PageSpinner />}>
                                        {children}
                                    </Suspense>
                                </OrgApplicationBar>
                            ) : (
                                <Suspense fallback={<PageSpinner />}>
                                    {children}
                                </Suspense>
                            )}
                        </Content>
                    </Layout>
                </Layout>
            </Layout>
        </>
    );
}
